import React, { useState, useEffect, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { slideshowImages } from "../../config/imageAssets";
import { motion } from "framer-motion";
import { PageName } from "../../types";
import { useTranslation } from "react-i18next";

interface LandingPageProps {
  onNavigate: (page: PageName, section?: string) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const totalSlides = slideshowImages.length;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previousImageIndex, setPreviousImageIndex] = useState<number | null>(null);
  const [showButtons, setShowButtons] = useState(false);

  const changeSlide = useCallback((getNextIndex: number | ((prev: number) => number)) => {
    if (totalSlides === 0) {
      return;
    }

    setCurrentImageIndex((prevIndex) => {
      const rawNextIndex =
        typeof getNextIndex === "function" ? getNextIndex(prevIndex) : getNextIndex;
      const normalizedIndex = ((rawNextIndex % totalSlides) + totalSlides) % totalSlides;

      if (normalizedIndex === prevIndex) {
        return prevIndex;
      }

      setPreviousImageIndex(prevIndex);
      return normalizedIndex;
    });
  }, [totalSlides]);

  // Use useCallback for event handlers
  
  const handleLearnMore = useCallback(() => {
    onNavigate("home", "about-us"); // Navigate to home page and specify about-us section
  }, [onNavigate]);

  useEffect(() => {
    // Cleaner interval management
    const interval = setInterval(() => {
      changeSlide((prevIndex) => prevIndex + 1);
    }, 5000); // Increased to 5 seconds for better UX
    
    const buttonTimer = setTimeout(() => {
      setShowButtons(true);
    }, 500);

    return () => {
      clearInterval(interval);
      clearTimeout(buttonTimer);
    };
  }, [totalSlides, changeSlide]);

  useEffect(() => {
    if (previousImageIndex === null) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setPreviousImageIndex(null);
    }, 1000); // match transition duration

    return () => clearTimeout(timeout);
  }, [currentImageIndex, previousImageIndex]);

  useEffect(() => {
    if (totalSlides <= 1) {
      return undefined;
    }

    const nextIndex = (currentImageIndex + 1) % totalSlides;
    const nextImageSrc = slideshowImages[nextIndex]?.src;

    if (!nextImageSrc) {
      return undefined;
    }

    const img = new Image();
    img.decoding = "async";
    img.src = nextImageSrc;

    return () => {
      img.onload = null;
    };
  }, [currentImageIndex, totalSlides]);

  return (
    <div className="relative h-screen w-full">
      {/* Slideshow */}
      <div className="absolute inset-0 overflow-hidden">
        {totalSlides > 0 &&
          (previousImageIndex === null
            ? [currentImageIndex]
            : [currentImageIndex, previousImageIndex]
          ).map((index) => {
            const image = slideshowImages[index];
            const isActiveSlide = index === currentImageIndex;

            if (!image) {
              return null;
            }

            return (
              <motion.div
                key={image.src}
                className="absolute inset-0 overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: isActiveSlide ? 1 : 0,
                  zIndex: isActiveSlide ? 2 : 1
                }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              >
                <motion.img
                  src={image.src}
                  alt={image.alt || `Zdjęcie WOPR Chodzież ${index + 1}`}
                  className="h-full w-full object-cover"
                  loading={isActiveSlide ? "eager" : "lazy"}
                  decoding="async"
                  sizes="100vw"
                  fetchPriority={isActiveSlide && index === 0 ? "high" : "auto"}
                  initial={{ scale: 1.0 }}
                  animate={{ scale: 1.1 }}
                  transition={{ duration: 7, ease: "linear" }}
                />
                <div className="absolute inset-0 bg-black/40" />
              </motion.div>
            );
          })}
      </div>

      {/* Centered Title with Logo */}
      <div className="absolute top-1/2 left-1/2 z-10 flex items-center justify-center -translate-x-1/2 -translate-y-1/2">
        <motion.img
          src={`${process.env.PUBLIC_URL}/images/landing_page/wopr_logo.png`}
          alt="WOPR Logo"
          className="h-10 sm:h-18 md:h-20 lg:h-28 mr-2 sm:mr-3 md:mr-4 lg:mr-5"
          loading="lazy"
          decoding="async"
          initial={{ opacity: 0, scale: 0.5, rotate: -15 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{
            duration: 0.8,
            type: "spring",
            bounce: 0.5
          }}
        />
        <motion.h1
          className="text-white font-bold text-2xl sm:text-3xl md:text-5xl lg:text-6xl drop-shadow-lg whitespace-nowrap"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            type: "spring",
            stiffness: 100
          }}
        >
          {t('landing.title', 'Chodzieskie WOPR')}
        </motion.h1>
      </div>

      {/* Navigation arrows */}

      {/* Down arrow for Learn More */}
      <motion.div
        className="absolute bottom-16 left-1/2 flex z-10"
        initial={{ opacity: 0, x: "-50%", y: 20 }}
        animate={{ opacity: showButtons ? 1 : 0, x: "-50%", y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <motion.button
          onClick={handleLearnMore}
          className="rounded-full bg-white/20 p-3 text-white backdrop-blur-sm"
          aria-label={t('landing.scroll_down', 'Przewiń w dół aby dowiedzieć się więcej')}
          animate={{ y: [0, 10, 0] }}
          whileHover={{ scale: 1.1, backgroundColor: "rgba(255, 255, 255, 0.3)" }}
          whileTap={{ scale: 0.95 }}
          transition={{
            y: {
              duration: 1,
              repeat: Infinity,
              ease: "easeInOut"
            },
            default: { duration: 0.2 }
          }}
        >
          <ChevronDown size={32} />
        </motion.button>
      </motion.div>
    </div>
  );
}

export default LandingPage;